import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SortingRule } from 'react-table';
import { Col, Row } from 'reactstrap';
import TableComponent from '../TableComponent';
import { PartsAndServicesQuotationDTO } from '../../models/Quotation/PartsAndServicesQuotationDTO';
import { Utilities } from '../../helpers/Utilities';
import ButtonComponent from '../ButtonComponent';
import { SupplierQuotationService } from '../../services/SupplierQuotationService';
import InputComponent from '../InputComponent';
import Toast from "../../helpers/Toast";
import SearchSelectComponent, { SelectItemsProps } from "../../components/SearchSelectComponent";
import { SearchPartByName } from '../../models/Parts/SearchPartByName';
import { PartService } from '../../services/PartService';
import ModalComponent from '../ModalComponent';

export type PartsTableComponentType = {
    isEditable: boolean;
    reloadParts: boolean;
    goToPage: number;
    currentPage: number;
    isComplete: boolean;
    onPartsChange: (parts: PartsAndServicesQuotationDTO[]) => void;
    initialParts: PartsAndServicesQuotationDTO[];
    quotationType: string;
    id: number;
};

const DetailsQuotationPartsTable: React.FC<PartsTableComponentType> = ({
    isEditable,
    reloadParts,
    goToPage,
    currentPage,
    isComplete,
    onPartsChange,
    initialParts,
    quotationType,
    id
  }) => {
    const intl = useIntl();
    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
    const [parts, setParts] = useState<PartsAndServicesQuotationDTO[]>(initialParts || []);
    const [totalPages, setTotalPages] = useState<number>(0);
    const pageSizes: number[] = [5, 15, 30];
    const [pageSize, setPageSize] = useState<number>(5);
    const [initialSortBy] = useState<SortingRule<object>[]>([]);
    const [quantity, setQuantity] = useState<number>(0);
    const [sellingPrice, setSellingPrice] = useState<number>(0);
    let [partId, setPartId] = useState<string>("NaN");
    const [name, setName] = useState<string>("");
    const [costPrice, setCostPrice] = useState<number>(0);
    const partService = new PartService();
    const [selectedPart, setSelectedPart] = useState<SelectItemsProps | null>(null);
    const [inputpart, setInputPart] = useState<string>("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmIndex, setConfirmIndex] = useState<number | null>(null);
       
    
    const openDeleteConfirm = (index: number) => {
        setConfirmIndex(index);
        setConfirmOpen(true);
    };
    const searchPart = async (name: string): Promise<SelectItemsProps[]> => {
        const searchParams: SearchPartByName = { name };
        const response = await partService.SearchPartByName(searchParams);

        if (!response.success || response.obj === null) {
            Toast.Show("error", response.message);
            return [];
        }

        return response.obj.map((element) => ({
            value: element.erpId,
            label: element.name,
        }));
    };

    const fetchPartDetails = async (erpId: string) => {
        const response = await partService.GetByIdErp(erpId);

        if (response.success && response.obj) {
            const part = response.obj;
            setPartId(part.erpId); 
            setName(part.name);
            setSellingPrice(part.sellingPrice ?? 0); 
            setCostPrice(part.costPrice ?? 0);
        } else {
            Toast.Show("error", response.message);
        }
    };

    useEffect(() => {
        onPartsChange(parts);
            setParts(parts);
      }, [onPartsChange]);
    
    const deletePart = (index: number) => {
        
        setParts(prevParts => {
            const updatedParts = prevParts.filter((_, i) => i !== index);
            onPartsChange(updatedParts); 
    
            return updatedParts; 
        });
    };
    useEffect(() => {
        setParts(initialParts);

        onPartsChange(initialParts);
    }, [initialParts]);

    useEffect(() => {
        setParts(parts);
    }, [parts]);

    useEffect(() => {
        RemoveFilters();
    }, [parts]);

    const fetchData = async () => { 
        setParts(parts);
    }
    const AddToTable = async () => {
    if (partId === "NaN" || name === "" || quantity === 0) {
        Toast.Show('error', intl.formatMessage({ id: "Screens.Quotation.Parts.Info.FillAllFields" }));
        return;
    }
 
    if (parts.some(part => part.partId === String(partId))) {
        Toast.Show('error', intl.formatMessage({ id: "Screens.Quotation.Parts.Info.CodeAlreadyExists" }));
        return;
    }

    const newPart: PartsAndServicesQuotationDTO = {
        partId: String(partId),
        partName: name,
        quantity: quantity,
        costPrice: costPrice,
        sellingPrice: sellingPrice,
        totalSellingPrice: quantity * sellingPrice,
    };

    const updatedParts = [...parts, newPart];
    setParts(updatedParts);
    onPartsChange(updatedParts);
    console.log("UpdatedParts:", updatedParts);
};
    

    const RemoveFilters = () => {
        setPartId("");
        setName("");
        setQuantity(0);
        setSellingPrice(0);
        setCostPrice(0);
    }
    const columns = React.useMemo(() => {
        if (quotationType === 'SupplierQuotation') {
            return [
                {
                    
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Code" />,
                    accessor: "partId",
                    width: 15,
                    Cell: ({ cell }: { cell: { value: number } }) => {
                        const partId = cell.value;
                        return (
                            <div>
                                {partId === 0 ? (
                                    <FormattedMessage id="Screens.Quotation.Parts.Message.NoPart" />
                                ) : (
                                    partId
                                )}
                            </div>
                        );
                    },
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Name" />,
                    accessor: "partName",
                    width: 35,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Quantity" />,
                    accessor: "quantity",
                    width: 25,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.CostPrice" />,
                    accessor: "costPrice",
                    width: 25,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Actions"/>,
                    width: 20,
                    Cell: ({ row: { index } }: { row: { index: number } }) => (
                        <ButtonComponent
                            style={{ margin: 'auto', width: '40%'}}
                            text= {intl.formatMessage({ id: "Screens.Quotation.Parts.Remove"})}
                            onClick={() => deletePart(index)}
                            disabled={isComplete || !isEditable}
                        />
                    ),
                },
            ];
        }
    
        if (quotationType === 'ClientQuotation') {
            return [
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Code" />,
                    accessor: "partId",
                    width: 15,
                    Cell: ({ cell }: { cell: { value: number } }) => {
                        const partId = cell.value;
                        return (
                            <div>
                                {partId === 0 ? (
                                    <FormattedMessage id="Screens.Quotation.Parts.Message.NoPart" />
                                ) : (
                                    partId
                                )}
                            </div>
                        );
                    },
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Name" />,
                    accessor: "partName",
                    width: 25,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Quantity" />,
                    accessor: "quantity",
                    width: 15,
                    Cell: ({ row, value }: { row: { index: number }; value: number }) => {
                        if (!isEditable || isComplete) {
                        return <span>{value}</span>;
                        }
                        return (
                        <input
                            type="number"
                            min={1}
                            value={value}
                            onChange={(e) => {
                                const newQuantity = parseInt(e.target.value);
                                if (!isNaN(newQuantity) && newQuantity > 0) {
                                    setParts(prevParts => {
                                    const updatedParts = [...prevParts];
                                    updatedParts[row.index] = {
                                        ...updatedParts[row.index],
                                        quantity: newQuantity,
                                        totalSellingPrice: newQuantity * updatedParts[row.index].sellingPrice,
                                    };
                                    onPartsChange(updatedParts);
                                    return updatedParts;
                                    });
                                }
                                }}

                            style={{ width: '80px' }}
                        />
                        );
                    },
                },
                {
                   Header: <FormattedMessage id="Screens.Quotation.Parts.Label.CostPrice" />,
                    accessor: "costPrice",
                    width: 15,
                    Cell: ({ row, value }: { row: { index: number }; value: number }) => {
                        if (!isEditable || isComplete) {
                        return <span>{value.toFixed(2)}</span>;
                        }

                        return (
                        <input
                            type="number"
                            min={0}
                            step={0.01}
                            value={value}
                            onChange={(e) => {
                                const newCostPrice = parseFloat(e.target.value);
                                if (!isNaN(newCostPrice) && newCostPrice >= 0) {
                                    setParts(prevParts => {
                                    const updatedParts = [...prevParts];
                                    updatedParts[row.index] = {
                                        ...updatedParts[row.index],
                                        costPrice: newCostPrice,
                                    };
                                    onPartsChange(updatedParts);
                                    return updatedParts;
                                    });
                                }
                                }}

                            style={{ width: '80px' }}
                        />
                        );
                    },
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.SellingPrice" />,
                    accessor: "sellingPrice",
                    width: 15,
                    Cell: ({ row, value }: { row: { index: number }; value: number }) => {
                        if (!isEditable || isComplete) {
                        return <span>{value}</span>;
                        }

                        return (
                        <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={value}
                            onChange={(e) => {
                            const newSellingPrice = parseFloat(e.target.value);
                            if (!isNaN(newSellingPrice) && newSellingPrice >= 0) {
                                setParts(prevParts => {
                                const updatedParts = [...prevParts];
                                const currentPart = updatedParts[row.index];
                                updatedParts[row.index] = {
                                    ...currentPart,
                                    sellingPrice: newSellingPrice,
                                    totalSellingPrice: currentPart.quantity * newSellingPrice, // atualiza o total no fim da linha
                                };
                                onPartsChange(updatedParts);
                                return updatedParts;
                                });
                            }
                            }}
                            style={{ width: '80px' }}
                        />
                        );
                },
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.TotalValue" />,
                    accessor: "totalSellingPrice",
                    width: 15,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Actions"/>,
                    width: 20,
                    Cell: ({ row: { index } }: { row: { index: number } }) => (
                        <ButtonComponent
                            style={{ margin: 'auto', width: '40%'}}
                            text= {intl.formatMessage({ id: "Screens.Quotation.Parts.Remove"})}
                            onClick={() => openDeleteConfirm(index)}
                            disabled={isComplete || !isEditable}
                        />
                    ),
                },
            ];
        }
        return [];
    }, [quotationType, isComplete]);
    return (
        
        <div>
            <ModalComponent
            IsOpen={confirmOpen}
            title={intl.formatMessage({ id: "Screens.Quotation.Parts.ConfirmRemove.Title" })}
            text={intl.formatMessage(
                { id: "Screens.Quotation.Parts.ConfirmRemove.Text" },
                { name: parts[confirmIndex!]?.partName }
            )}
            OnClickLeft={() => {
                if (confirmIndex !== null) {
                deletePart(confirmIndex);
                }
                setConfirmOpen(false);
            }}
            OnClickRight={() => setConfirmOpen(false)}
            buttonLeft={"Sim"}
            buttonRight={"Não"}
            />
            {isEditable && (
                <>
            <Col xl={12} lg={12} md={12} sm={12} xs={12}>
            <Row xl={12} lg={12} md={12} sm={12} xs={12}>
                    <SearchSelectComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                        label={intl.formatMessage({ id: "Pesquisar peças" })}
                        required={true}
                        loadOptions={searchPart} 
                        onChange={(e: SelectItemsProps) => {
                            setSelectedPart(e);
                            setInputPart(e.value);
                            fetchPartDetails(e.value);
                        }}
                        value={selectedPart}
                        style={{ width: "100%" }}
                        />
                        <InputComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            value={partId}
                            label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.Code" })}
                            readOnly={true}
                        />
                        
                        <InputComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            value={name}
                            label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.Name" })}
                            readOnly={true}
                        />
                        <InputComponent
                            xl={1.5} lg={2} md={4} sm={8} xs={12}
                            style={{ width: "100%" }}
                            type={"number"}
                            value={quantity}
                            label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.Quantity" })}
                            required={true}
                            readOnly={isComplete}
                            onChange={(e: number) => setQuantity(e)}
                        />
                        <InputComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                            style={{ width: "100%" }}
                            type={"number"}
                            value={costPrice}
                            label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.CostPrice" })}
                            readOnly={isComplete}
                            onChange={(e: number) => setCostPrice(e)}
                        />
                        {quotationType === 'ClientQuotation' && (
                            <InputComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                                style={{ width: "100%" }}
                                type={"number"}
                                value={sellingPrice}
                                label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.SellingPrice" })}
                                readOnly={isComplete}
                                onChange={(e: number) => setSellingPrice(e)}
                            />
                        )}
                    </Row>
                </Col>
                <Row>
                    <Col xl={1} lg={2} md={3} sm={4} xs={6}>
                        <ButtonComponent
                            style={{ marginTop: '10%', width: '100%'}}
                            text={intl.formatMessage({ id: "Screens.Assistances.Parts.Label.ClearBoxes" })}
                            onClick={() => RemoveFilters()}
                            disabled={isComplete}
                        />
                    </Col>
                    <Col xl={1} lg={2} md={3} sm={4} xs={6}>
                        <ButtonComponent
                            style={{ marginTop: '10%',width: '100%'}}
                            text={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.Add" })}
                            onClick={() => AddToTable()}
                            disabled={isComplete}
                        />
                    </Col>
                </Row>
                </>
            )}
            <Row xl={12} lg={12} md={12} sm={12} xs={12}>
                <Row style={{ margin: "1rem 0px" }}>
                    <TableComponent
                        goToPage={goToPage}
                        loading={isLoadingTable}
                        pageCount={totalPages}
                        columns={columns.map(column => ({
                            ...column,
                            accessor: String(column.accessor) 
                        }))}
                        manualPagination={true}
                        fetchData={() => {}}
                        pageSizes={pageSizes}
                        initialPage={currentPage}
                        initialPageSize={pageSize}
                        initialSortBy={initialSortBy}
                        data={parts}
                        dontOrderLastCollumn={true}
                        dontOrderFirstCollumn={false}
                    />
                </Row>
            </Row>
        </div>
    )
}
export default DetailsQuotationPartsTable
